/**
 * Boarding Pass Generator Utility
 * Generates PDF boarding passes using HTML-to-PDF conversion
 * Uses native browser APIs (no external libraries)
 */

export const generateBoardingPassPDF = (boardingPassData, fileName = 'boarding-pass.pdf') => {
  try {
    // Create HTML content for the boarding pass
    const htmlContent = createBoardingPassHTML(boardingPassData);

    // Open a new window to display the boarding pass (no auto-print)
    const printWindow = window.open('', '', 'width=900,height=1000');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${fileName}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Arial', sans-serif;
            background: #f5f5f5;
            padding: 20px;
            line-height: 1.6;
          }
          
          .toolbar {
            background: white;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            display: flex;
            gap: 10px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            position: sticky;
            top: 0;
            z-index: 100;
          }
          
          .toolbar button {
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          
          .btn-print {
            background: #4CAF50;
            color: white;
          }
          
          .btn-print:hover {
            background: #45a049;
          }
          
          .btn-download {
            background: #2196F3;
            color: white;
          }
          
          .btn-download:hover {
            background: #0b7dda;
          }
          
          .btn-close {
            background: #f44336;
            color: white;
            margin-left: auto;
          }
          
          .btn-close:hover {
            background: #da190b;
          }
          
          .preview-container {
            background: white;
            padding: 40px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            max-width: 900px;
            margin: 0 auto;
          }
          
          @page {
            size: A4;
            margin: 0;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .toolbar {
              display: none;
            }
            
            .preview-container {
              box-shadow: none;
              padding: 0;
              max-width: 100%;
            }
          }
          
          .boarding-pass-container {
            max-width: 210mm;
            background: white;
            border: 3px solid #333;
            padding: 0;
            margin: 0 auto;
            font-family: 'Courier New', monospace;
          }
          
          .pass-header {
            background: linear-gradient(135deg, #d4af37 0%, #ffd200 100%);
            padding: 20px;
            text-align: center;
            border-bottom: 2px solid #333;
          }
          
          .pass-header h1 {
            font-size: 32px;
            color: #1a1a1a;
            margin: 0;
            font-weight: bold;
            letter-spacing: 2px;
          }
          
          .pass-header p {
            font-size: 12px;
            color: #333;
            margin: 5px 0 0 0;
          }
          
          .pass-content {
            padding: 20px;
          }
          
          .passenger-section {
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 1px dashed #333;
          }
          
          .passenger-name {
            font-size: 18px;
            font-weight: bold;
            color: #1a1a1a;
            margin-bottom: 5px;
            text-transform: uppercase;
          }
          
          .passenger-label {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .flight-section {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 1px dashed #333;
          }
          
          .flight-item {
            text-align: center;
          }
          
          .flight-label {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 5px;
          }
          
          .flight-value {
            font-size: 16px;
            font-weight: bold;
            color: #1a1a1a;
          }
          
          .airport-code {
            font-size: 28px;
            font-weight: bold;
            color: #d4af37;
            margin: 5px 0;
          }
          
          .airport-name {
            font-size: 11px;
            color: #666;
          }
          
          .arrow {
            font-size: 24px;
            color: #d4af37;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
          }
          
          .details-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 1px dashed #333;
          }
          
          .detail-box {
            text-align: center;
          }
          
          .detail-label {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 5px;
          }
          
          .detail-value {
            font-size: 14px;
            font-weight: bold;
            color: #1a1a1a;
          }
          
          .barcode-section {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px dashed #333;
          }
          
          .barcode-placeholder {
            font-size: 12px;
            color: #666;
            font-family: 'Courier New', monospace;
            letter-spacing: 2px;
            margin: 10px 0;
            font-weight: bold;
          }
          
          .booking-ref {
            font-size: 11px;
            color: #666;
            margin-top: 5px;
          }
          
          .footer-text {
            text-align: center;
            font-size: 9px;
            color: #999;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
          }
          
          .status-badge {
            display: inline-block;
            padding: 5px 10px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
            margin-top: 5px;
            background: #d4f1d4;
            color: #2d5016;
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <button class="btn-print" onclick="window.print()">🖨️ Print</button>
          <button class="btn-download" onclick="downloadPDF()">⬇️ Download as PDF</button>
          <button class="btn-close" onclick="window.close()">✕ Close</button>
        </div>
        <div class="preview-container">
          ${htmlContent}
        </div>
        <script>
          function downloadPDF() {
            // Trigger browser's print-to-PDF
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();

    return true;
  } catch (error) {
    console.error('Error generating boarding pass PDF:', error);
    throw error;
  }
};

/**
 * Create HTML content for boarding pass
 */
const createBoardingPassHTML = (data) => {
  const {
    booking_reference,
    flight,
    passengers,
    booking_status,
    passenger_count,
    seat_number,
    gate,
    boarding_time,
    departure_time
  } = data;

  const departureTime = new Date(flight.departure.datetime);
  const departureHours = departureTime.getHours().toString().padStart(2, '0');
  const departureMinutes = departureTime.getMinutes().toString().padStart(2, '0');

  const departureDateStr = departureTime.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const boardingTimeStr = boarding_time 
    ? new Date(boarding_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : 'TBA';

  // Generate a simple barcode-like string from booking reference
  const barcodeString = booking_reference.replace(/[^0-9]/g, '').padEnd(12, '0').substring(0, 12);

  return `
    <div class="boarding-pass-container">
      <div class="pass-header">
        <h1>BOARDING PASS</h1>
        <p>Electronic Boarding Pass - Valid for travel</p>
      </div>

      <div class="pass-content">
        <!-- Passenger Section -->
        <div class="passenger-section">
          <div class="passenger-label">Passenger Name</div>
          <div class="passenger-name">${passengers && passengers.length > 0 ? passengers[0].full_name.toUpperCase() : 'N/A'}</div>
          ${passengers && passengers.length > 1 ? `<div style="font-size: 11px; color: #666; margin-top: 5px;">+${passengers.length - 1} more passenger(s)</div>` : ''}
        </div>

        <!-- Flight Information -->
        <div class="flight-section">
          <div class="flight-item">
            <div class="flight-label">From</div>
            <div class="airport-code">${flight.departure.iata}</div>
            <div class="airport-name">${flight.departure.airport}</div>
          </div>
          <div class="arrow">→</div>
          <div class="flight-item">
            <div class="flight-label">To</div>
            <div class="airport-code">${flight.arrival.iata}</div>
            <div class="airport-name">${flight.arrival.airport}</div>
          </div>
        </div>

        <!-- Flight Details Grid -->
        <div class="details-grid">
          <div class="detail-box">
            <div class="detail-label">Flight</div>
            <div class="detail-value">${flight.flight_number}</div>
          </div>
          <div class="detail-box">
            <div class="detail-label">Date</div>
            <div class="detail-value">${departureDateStr}</div>
          </div>
          <div class="detail-box">
            <div class="detail-label">Departure</div>
            <div class="detail-value">${departureHours}:${departureMinutes}</div>
          </div>
        </div>

        <!-- Seat & Gate Information -->
        <div class="details-grid">
          <div class="detail-box">
            <div class="detail-label">Seat</div>
            <div class="detail-value">${seat_number || 'TBA'}</div>
          </div>
          <div class="detail-box">
            <div class="detail-label">Gate</div>
            <div class="detail-value">${gate || 'TBA'}</div>
          </div>
          <div class="detail-box">
            <div class="detail-label">Boarding</div>
            <div class="detail-value">${boardingTimeStr}</div>
          </div>
        </div>

        <!-- Aircraft Information -->
        <div style="text-align: center; font-size: 12px; color: #666; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px dashed #333;">
          <strong>Aircraft:</strong> ${flight.aircraft_name} (${flight.registration_number})
        </div>

        <!-- Barcode Section -->
        <div class="barcode-section">
          <div class="barcode-placeholder">║ ${barcodeString} ║</div>
          <div class="booking-ref">Booking Reference: ${booking_reference}</div>
          <div style="margin-top: 10px;">
            <span class="status-badge">VALID FOR TRAVEL</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="footer-text">
          <p>Please arrive at the airport at least 2 hours before departure.</p>
          <p>This is an electronic boarding pass. Please present this document at check-in.</p>
          <p>Generated on: ${new Date().toLocaleString('en-US')}</p>
          <p>© JetScanner 360 - All Rights Reserved</p>
        </div>
      </div>
    </div>
  `;
};

/**
 * Download boarding pass as PDF using print-to-PDF
 */
export const downloadBoardingPassPDF = (boardingPassData, fileName = 'boarding-pass.pdf') => {
  generateBoardingPassPDF(boardingPassData, fileName);
};

/**
 * Send boarding pass to email via backend
 */
export const sendBoardingPassToEmail = async (referenceNumber, apiBase) => {
  try {
    const response = await fetch(
      `${apiBase}/bookings/${referenceNumber}/send-boarding-pass`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending boarding pass to email:', error);
    throw error;
  }
};
